import {
  getAlphaCatalogueLabel,
  type AlphaCatalogue,
  type VehicleV2ResponseBody,
} from '@online-offer/shared';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {Nullish} from 'shared/web';

export type WltpFuelConsumptionData = {
  schema: 'WLTP';
  combined: string | Nullish;
  cityCenter: string | Nullish;
  outskirts: string | Nullish;
  countryRoads: string | Nullish;
  highway: string | Nullish;
  weighted: string | Nullish;
};

export type NedcFuelConsumptionData = {
  schema: 'NEDC';
  combined: string | Nullish;
  urban: string | Nullish;
  extraUrban: string | Nullish;
};

export type FuelConsumptionData = WltpFuelConsumptionData | NedcFuelConsumptionData;

export type WltpElectricalConsumptionData = {
  schema: 'WLTP';
  combined: string | Nullish;
  cityCenter: string | Nullish;
  outskirts: string | Nullish;
  countryRoads: string | Nullish;
  highway: string | Nullish;
  weightedCombined: string | Nullish;
};

export type NedcElectricalConsumptionData = {
  schema: 'NEDC';
  combined: string | Nullish;
};

export type ElectricalConsumptionData =
  | WltpElectricalConsumptionData
  | NedcElectricalConsumptionData;

function hasWltpFuelData(vehicle: VehicleV2ResponseBody): boolean {
  return (
    isNotNilOrEmpty(vehicle.fuelConsumptionWltpCombined) ||
    isNotNilOrEmpty(vehicle.fuelConsumptionWltpCityCenter) ||
    isNotNilOrEmpty(vehicle.fuelConsumptionWltpOutskirts) ||
    isNotNilOrEmpty(vehicle.fuelConsumptionWltpCountryRoads) ||
    isNotNilOrEmpty(vehicle.fuelConsumptionWltpHighway)
  );
}

function hasWltpElectricalData(vehicle: VehicleV2ResponseBody): boolean {
  return (
    isNotNilOrEmpty(vehicle.fuelConsumptionWltpCombinedKwh) ||
    isNotNilOrEmpty(vehicle.fuelConsumptionWltpCityCenterKwh) ||
    isNotNilOrEmpty(vehicle.fuelConsumptionWltpOutskirtsKwh) ||
    isNotNilOrEmpty(vehicle.fuelConsumptionWltpCountryRoadsKwh) ||
    isNotNilOrEmpty(vehicle.fuelConsumptionWltpHighwayKwh)
  );
}

function formatConsumptionValue(value: string | Nullish, unit: string | Nullish): string | null {
  if (!isNotNilOrEmpty(value)) {
    return null;
  }
  return unit ? `${value} ${unit}` : String(value);
}

export function getFuelConsumptionData(
  vehicle: VehicleV2ResponseBody | Nullish,
  alphaCatalogue: AlphaCatalogue | Nullish
): FuelConsumptionData | null {
  if (!vehicle || !alphaCatalogue) {
    return null;
  }

  const fuelUnitL100km = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'fuel_unit',
    key: 'FUEL_UNIT_L100KM',
    alphaCatalogue,
  });

  if (hasWltpFuelData(vehicle)) {
    return {
      schema: 'WLTP',
      combined: formatConsumptionValue(vehicle.fuelConsumptionWltpCombined, fuelUnitL100km),
      cityCenter: formatConsumptionValue(vehicle.fuelConsumptionWltpCityCenter, fuelUnitL100km),
      outskirts: formatConsumptionValue(vehicle.fuelConsumptionWltpOutskirts, fuelUnitL100km),
      countryRoads: formatConsumptionValue(vehicle.fuelConsumptionWltpCountryRoads, fuelUnitL100km),
      highway: formatConsumptionValue(vehicle.fuelConsumptionWltpHighway, fuelUnitL100km),
      weighted: formatConsumptionValue(vehicle.fuelConsumptionWltpWeighted, fuelUnitL100km),
    };
  }

  return {
    schema: 'NEDC',
    combined: formatConsumptionValue(vehicle.fuelConsumptionCombined, fuelUnitL100km),
    urban: formatConsumptionValue(vehicle.fuelConsumptionUrban, fuelUnitL100km),
    extraUrban: formatConsumptionValue(vehicle.fuelConsumptionExtraUrban, fuelUnitL100km),
  };
}

export function getElectricalConsumptionData(
  vehicle: VehicleV2ResponseBody | Nullish,
  alphaCatalogue: AlphaCatalogue | Nullish
): ElectricalConsumptionData | null {
  if (!vehicle || !alphaCatalogue) {
    return null;
  }

  const fuelUnitKwh100km = getAlphaCatalogueLabel({
    vehicleType: vehicle.type,
    propertyName: 'fuel_unit',
    key: 'FUEL_UNIT_KWH100KM',
    alphaCatalogue,
  });

  if (hasWltpElectricalData(vehicle)) {
    return {
      schema: 'WLTP',
      combined: formatConsumptionValue(vehicle.fuelConsumptionWltpCombinedKwh, fuelUnitKwh100km),
      cityCenter: formatConsumptionValue(
        vehicle.fuelConsumptionWltpCityCenterKwh,
        fuelUnitKwh100km
      ),
      outskirts: formatConsumptionValue(vehicle.fuelConsumptionWltpOutskirtsKwh, fuelUnitKwh100km),
      countryRoads: formatConsumptionValue(
        vehicle.fuelConsumptionWltpCountryRoadsKwh,
        fuelUnitKwh100km
      ),
      highway: formatConsumptionValue(vehicle.fuelConsumptionWltpHighwayKwh, fuelUnitKwh100km),
      weightedCombined: formatConsumptionValue(
        vehicle.fuelConsumptionWltpWeightedCombinedKwh,
        fuelUnitKwh100km
      ),
    };
  }

  return {
    schema: 'NEDC',
    combined: formatConsumptionValue(vehicle.powerConsumptionCombined, fuelUnitKwh100km),
  };
}
